import { withSyncLock } from "@/core/offline/sync-engine";
import { recordPendingDeletion } from "@/core/offline/db";
import { authFetch } from "@/core/utils/auth-fetch";

/**
 * Supprime une entité côté serveur puis localement (cascade).
 * Si offline ou timeout (3s), supprime localement et enregistre
 * la suppression pour sync ultérieure.
 *
 * @param entityType — type d'entité ("site", "zone", "campagne", "pin", "mesure", "photo")
 * @param id — identifiant de l'entité
 * @param apiPath — chemin API pour le DELETE serveur (ex: `/api/sites/${id}`)
 * @param cascadeDelete — fonction qui supprime l'entité et ses enfants dans IndexedDB
 */
export async function deleteWithOfflineFallback(
  entityType: string,
  id: string,
  apiPath: string,
  cascadeDelete: () => Promise<void>
): Promise<void> {
  await withSyncLock(async () => {
    let deletedOnServer = false;

    if (typeof navigator !== "undefined" && navigator.onLine) {
      try {
        const timeout = new Promise<never>((_, rej) =>
          setTimeout(() => rej(new Error("timeout")), 3000)
        );
        const response = await Promise.race([
          authFetch(apiPath, { method: "DELETE" }),
          timeout,
        ]);
        if (response.ok || response.status === 404) {
          // 404 = déjà supprimé côté serveur, c'est OK
          deletedOnServer = true;
        } else {
          // Erreur HTTP 4xx/5xx = réelle, on propage
          const data = await response.json().catch(() => ({}));
          throw new Error(data.error ?? `HTTP ${response.status}`);
        }
      } catch (err) {
        // Erreur réseau / timeout / serveur injoignable → on bascule en mode offline
        // (TypeError depuis fetch, message "timeout", ou toute autre panne réseau).
        // Seules les erreurs HTTP explicites sont propagées (voir ci-dessus).
        const msg = err instanceof Error ? err.message : String(err);
        const isNetworkOrTimeout =
          err instanceof TypeError ||
          msg === "timeout" ||
          msg.startsWith("Failed to fetch") ||
          msg.startsWith("NetworkError");
        if (!isNetworkOrTimeout) {
          throw err;
        }
      }
    }

    if (!deletedOnServer) {
      await recordPendingDeletion(entityType, id);
    }

    await cascadeDelete();
  });
}
